// Fiche produit : galerie, changement de variante, quantite, ajout au
// panier sans quitter la page. Aucune dependance.

class MxProduit extends HTMLElement {
  connectedCallback() {
    this.variantes = this.lis('[data-variantes]') ?? [];
    this.brancheGalerie();
    this.brancheOptions();
    this.brancheQuantite();
    this.brancheAjout();
  }

  lis(selecteur) {
    const noeud = this.querySelector(selecteur);
    if (!noeud) return null;
    try {
      return JSON.parse(noeud.textContent);
    } catch {
      return null;
    }
  }

  /* ---------- Galerie ---------- */
  brancheGalerie() {
    const principale = this.querySelector('[data-galerie-principale] img');
    const vignettes = [...this.querySelectorAll('[data-media]')];
    if (!principale || !vignettes.length) return;

    vignettes.forEach((vignette) => {
      vignette.addEventListener('click', () => {
        principale.src = vignette.dataset.media;
        // srcset garderait la priorite sur src : il faut le retirer.
        principale.removeAttribute('srcset');
        vignettes.forEach((v) => v.removeAttribute('aria-current'));
        vignette.setAttribute('aria-current', 'true');
      });
    });
  }

  /* ---------- Variantes ---------- */
  brancheOptions() {
    this.radios = [...this.querySelectorAll('[data-option]')];
    if (!this.radios.length) return;

    this.radios.forEach((radio) => {
      radio.addEventListener('change', () => this.changeVariante());
    });
  }

  variantesChoisies() {
    const choix = [];
    this.radios.forEach((radio) => {
      if (radio.checked) choix[Number(radio.dataset.option) - 1] = radio.value;
    });
    return choix;
  }

  changeVariante() {
    const choix = this.variantesChoisies();
    const trouvee = this.variantes.find((v) =>
      choix.every((val, i) => val === undefined || v.options[i] === val)
    );
    if (!trouvee) return;

    const champId = this.querySelector('[data-id-variante]');
    if (champId) champId.value = trouvee.id;

    const prix = this.querySelector('[data-prix]');
    if (prix) prix.textContent = this.monnaie(trouvee.price);

    const barre = this.querySelector('[data-prix-barre]');
    if (barre) {
      const solde = trouvee.compare_at_price && trouvee.compare_at_price > trouvee.price;
      barre.hidden = !solde;
      if (solde) barre.textContent = this.monnaie(trouvee.compare_at_price);
    }

    const bouton = this.querySelector('[data-ajouter]');
    const libelle = this.querySelector('[data-libelle-ajout]');
    if (bouton) bouton.disabled = !trouvee.available;
    if (libelle) libelle.textContent = trouvee.available ? 'Ajouter au panier' : 'Épuisé';

    // L'adresse suit la variante choisie, pour que le lien soit partageable.
    const url = new URL(location.href);
    url.searchParams.set('variant', trouvee.id);
    history.replaceState({}, '', url);
  }

  /* Reprend le format monetaire de la boutique plutot que celui du
     navigateur : sans cela, changer de variante changeait aussi la
     presentation du prix, qui passait de « EUR2,49 » a « 2,49 EUR ». */
  monnaie(centimes) {
    const format = this.dataset.formatMonnaie;
    if (!format) return (centimes / 100).toFixed(2);

    const avecVirgule = (n, separateurMille) =>
      (n / 100).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/, separateurMille);
    const avecPoint = (n, separateurMille) =>
      (n / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/, separateurMille);
    const sansDecimale = (n, separateur) =>
      String(Math.round(n / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, separateur);

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, jeton) => {
      switch (jeton) {
        case 'amount': return avecPoint(centimes, ',');
        case 'amount_with_comma_separator': return avecVirgule(centimes, '.');
        case 'amount_with_apostrophe_separator': return avecPoint(centimes, "'");
        case 'amount_no_decimals': return sansDecimale(centimes, ',');
        case 'amount_no_decimals_with_comma_separator': return sansDecimale(centimes, '.');
        case 'amount_no_decimals_with_space_separator': return sansDecimale(centimes, ' ');
        case 'amount_with_space_separator': return avecVirgule(centimes, ' ');
        default: return '';
      }
    }).trim();
  }

  /* ---------- Quantite ---------- */
  brancheQuantite() {
    const champ = this.querySelector('[data-champ-quantite]');
    if (!champ) return;
    const pas = (delta) => {
      champ.value = Math.max(1, Number(champ.value || 1) + delta);
      champ.dispatchEvent(new Event('change', { bubbles: true }));
    };
    this.querySelector('[data-quantite-moins]')?.addEventListener('click', () => pas(-1));
    this.querySelector('[data-quantite-plus]')?.addEventListener('click', () => pas(1));
  }

  /* ---------- Ajout au panier ---------- */
  brancheAjout() {
    const forme = this.querySelector('[data-forme-produit]');
    if (!forme) return;

    forme.addEventListener('submit', async (ev) => {
      ev.preventDefault();
      const bouton = this.querySelector('[data-ajouter]');
      const libelle = this.querySelector('[data-libelle-ajout]');
      const avant = libelle?.textContent;

      bouton.disabled = true;
      if (libelle) libelle.textContent = 'Ajout…';

      try {
        const rep = await fetch('/cart/add.js', { method: 'POST', body: new FormData(forme) });
        if (!rep.ok) throw new Error(rep.status);

        document.dispatchEvent(new CustomEvent('mx:panier:rafraichir'));
        document.dispatchEvent(new CustomEvent('mx:panier:ouvrir'));
        if (libelle) libelle.textContent = 'Ajouté';
        setTimeout(() => { if (libelle) libelle.textContent = avant; }, 1400);
      } catch (e) {
        console.error('Produit : ajout au panier impossible', e);
        if (libelle) libelle.textContent = avant;
        // Repli : on laisse le formulaire partir normalement.
        forme.submit();
      } finally {
        bouton.disabled = false;
      }
    });
  }
}

customElements.define('mx-produit', MxProduit);
